import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Divider,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { Psychology, Science, Timeline, CheckCircle, Send } from '@mui/icons-material';
import axios from 'axios';

const LeadAI = () => {
  const API_BASE = useMemo(() => (
    process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '') || 'https://social-media-backend.andrewrkloc.workers.dev'
  ), []);

  const [intentText, setIntentText] = useState('Need a quote for a kitchen remodel this week. What’s the price range?');
  const [intentIndustry, setIntentIndustry] = useState('real-estate');
  const [intentResult, setIntentResult] = useState(null);
  const [loadingIntent, setLoadingIntent] = useState(false);

  const [lead, setLead] = useState({
    firstName: 'Ava',
    lastName: 'Brooks',
    industry: 'plumbing',
    location: 'Austin, TX',
  });
  const [dossier, setDossier] = useState(null);
  const [loadingDossier, setLoadingDossier] = useState(false);

  const [experiment, setExperiment] = useState('email_subjects_v1');
  const [variants, setVariants] = useState('A,B,C');
  const [choice, setChoice] = useState(null);
  const [reported, setReported] = useState(false);
  const [loadingExp, setLoadingExp] = useState(false);

  const callIntent = async () => {
    setLoadingIntent(true);
    setIntentResult(null);
    try {
      const { data } = await axios.post(`${API_BASE}/api/lead/intent/score`, {
        text: intentText,
        industry: intentIndustry,
      });
      setIntentResult(data);
    } catch (e) {
      setIntentResult({ success: false, error: e.message });
    } finally {
      setLoadingIntent(false);
    }
  };

  const callDossier = async () => {
    setLoadingDossier(true);
    setDossier(null);
    try {
      const { data } = await axios.post(`${API_BASE}/api/lead/dossier/generate`, { lead });
      setDossier(data.dossier);
    } catch (e) {
      setDossier({ error: e.message });
    } finally {
      setLoadingDossier(false);
    }
  };

  const chooseVariant = async () => {
    setLoadingExp(true);
    setChoice(null);
    setReported(false);
    try {
      const { data } = await axios.post(`${API_BASE}/api/experiments/choose`, {
        experiment,
        variants: variants.split(',').map(s => s.trim()).filter(Boolean),
      });
      setChoice(data.choice);
    } catch (e) {
      setChoice(null);
    } finally {
      setLoadingExp(false);
    }
  };

  const reportOutcome = async (success) => {
    try {
      await axios.post(`${API_BASE}/api/experiments/report`, {
        experiment,
        variant: choice,
        success,
      });
      setReported(true);
    } catch (e) {
      setReported(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Science color="primary" /> Lead AI POC
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Psychology color="primary" /> Intent Scoring
              </Typography>
              <TextField
                label="Sample message/comment"
                value={intentText}
                onChange={(e) => setIntentText(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Industry</InputLabel>
                <Select value={intentIndustry} label="Industry" onChange={(e) => setIntentIndustry(e.target.value)}>
                  <MenuItem value="real-estate">Real Estate</MenuItem>
                  <MenuItem value="plumbing">Plumbing</MenuItem>
                  <MenuItem value="healthcare">Healthcare</MenuItem>
                  <MenuItem value="fitness">Fitness</MenuItem>
                  <MenuItem value="attorney">Attorney</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" onClick={callIntent} disabled={loadingIntent} endIcon={<Send />}>
                {loadingIntent ? 'Scoring…' : 'Score Intent'}
              </Button>
              {intentResult && (
                <Box sx={{ mt: 2 }}>
                  {intentResult.success === false ? (
                    <Alert severity="error">{intentResult.error || 'Error'}</Alert>
                  ) : (
                    <>
                      <Typography variant="body2">Score: <b>{intentResult.score}</b> ({intentResult.urgency})</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>Recommendation: {intentResult.recommendation}</Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary">Reasons:</Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {(intentResult.reasons || []).map((r, i) => (
                          <Chip key={i} label={r} size="small" />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Timeline color="primary" /> Lead Dossier
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="First name" value={lead.firstName} onChange={(e) => setLead({ ...lead, firstName: e.target.value })} fullWidth />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Last name" value={lead.lastName} onChange={(e) => setLead({ ...lead, lastName: e.target.value })} fullWidth />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Industry" value={lead.industry} onChange={(e) => setLead({ ...lead, industry: e.target.value })} fullWidth />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Location" value={lead.location} onChange={(e) => setLead({ ...lead, location: e.target.value })} fullWidth />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={callDossier} disabled={loadingDossier}>
                  {loadingDossier ? 'Generating…' : 'Generate Dossier'}
                </Button>
              </Box>
              {dossier && (
                <Box sx={{ mt: 2 }}>
                  {dossier.error ? (
                    <Alert severity="error">{dossier.error}</Alert>
                  ) : (
                    <>
                      <Typography variant="subtitle2">{dossier.name} — {dossier.industry} — {dossier.location}</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>{dossier.summary}</Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="text.secondary">Best angles</Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {(dossier.bestAngles || []).map((r, i) => (
                          <Chip key={i} label={r} size="small" />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Experimentation (POC)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField label="Experiment Key" value={experiment} onChange={(e) => setExperiment(e.target.value)} fullWidth />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField label="Variants (comma-separated)" value={variants} onChange={(e) => setVariants(e.target.value)} fullWidth />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button variant="contained" onClick={chooseVariant} disabled={loadingExp}>
                    {loadingExp ? 'Choosing…' : 'Choose Variant'}
                  </Button>
                </Grid>
              </Grid>
              {choice && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip color="primary" label={`Chosen: ${choice}`} />
                  <Button variant="outlined" onClick={() => reportOutcome(true)} startIcon={<CheckCircle color="success" />}>Report Success</Button>
                  <Button variant="outlined" onClick={() => reportOutcome(false)}>Report Fail</Button>
                  {reported && <Chip color="success" label="Reported" />}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LeadAI;


